import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import * as benchlingUtil from 'BenchlingUtil';
import navigateToURL from 'lib/navigateToURL';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {GRAY_LIGHT} from 'ui-library/Colors';
import {BasicEvent} from 'util/Types';

import {ROLES} from './Menu';
import {MenuHeight, OptionContentType} from './MenuConstants';

export interface OptionContainerProps<T> {
  children: React.ReactNode;
  className?: string;
  linkClassName?: string;
  download?: string;
  href?: string;
  openInNewTab?: boolean;
  contentType?: OptionContentType;
  isDisabled?: boolean;
  isHovered?: boolean;
  isSelected?: boolean;
  onHover?: () => void;
  onMouseOut?: () => void;
  onMouseUp?: () => void;
  onSelect?: ((value?: T) => void) | null;
  shouldEllipsize: boolean;
  hasBorder?: boolean;
  heightOverride?: MenuHeight;
  value?: T;
  skipIconOverride?: boolean;
  _role?: string;
  _submenu?: React.ReactNode | null;
  disableEventPropagation?: boolean;
  dataTestAttrs?: DataTestAttrs;
  /** React ref for the focusable menu option element (the <a> or <div>) */
  optionRef?: React.RefObject<HTMLElement>;
}

/**
 * OptionContainer is the container component used in Option: it handles interactions (selection, hovering)
 * as well as some core attributes like isDisabled.
 */
class OptionContainer<T> extends React.Component<OptionContainerProps<T>> {
  static propTypes = {
    // The content of the option.
    children: PropTypes.node.isRequired,
    className: PropTypes.string,
    linkClassName: PropTypes.string,
    download: PropTypes.string,
    // If specified, then this option acts as a link pointed to this URL.
    href: PropTypes.string,
    openInNewTab: PropTypes.bool,
    isDisabled: PropTypes.bool,
    contentType: PropTypes.oneOf(Object.values(OptionContentType)),
    // If a value is not passed, it defaults to mouse hovering
    isHovered: PropTypes.bool,
    // Whether the option is selected.
    isSelected: PropTypes.bool,
    onHover: PropTypes.func,
    onMouseOut: PropTypes.func,
    onMouseUp: PropTypes.func,
    // Handler for selection, called with (value)
    onSelect: PropTypes.func,
    shouldEllipsize: PropTypes.bool.isRequired,
    hasBorder: PropTypes.bool,
    heightOverride: PropTypes.string,
    value: PropTypes.any, // eslint-disable-line react/forbid-prop-types

    // Do not pass in `submenu` -- it will be passed in automatically.
    _submenu: PropTypes.node,
    // Disables bubbling up events to parent elements on selection.
    disableEventPropagation: PropTypes.bool,
  };

  static defaultProps = {
    isDisabled: false,
    isSelected: false,
    contentType: OptionContentType.ANCHOR,
    _role: ROLES.OPTION,
    shouldEllipsize: false,
    disableEventPropagation: false,
  };

  containerRef = React.createRef<HTMLLIElement>();
  getRef = (): HTMLLIElement | null => this.containerRef.current;

  _getOnSelectHandler = (): ((e: BasicEvent) => void) | null => {
    if (this.props.href) {
      return (e: BasicEvent) => {
        if (this.props.disableEventPropagation) {
          e.stopPropagation();
        }
        if (!this.props.openInNewTab) {
          const relativeURL = benchlingUtil.getRelativeURL(this.props.href!);
          if (relativeURL == null) {
            window.location.href = this.props.href!;
          } else {
            navigateToURL(relativeURL);
          }
        }
      };
    } else if (this.props.onSelect && !this.props.isDisabled) {
      return (e: BasicEvent) => {
        if (this.props.disableEventPropagation) {
          e.stopPropagation();
        }
        this.props.onSelect!(this.props.value);
      };
    }
    return null;
  };

  _handleSelect = (e: BasicEvent): void => {
    if (this.props._submenu != null) {
      // Don't allow click event to bubble up; it will go all the way to `AbsoluteDropdownContainer` which
      // will, in most cases, `closeOnInsideClick`
      e.stopPropagation();
    }
    const selectHandler = this._getOnSelectHandler();
    if (selectHandler) {
      selectHandler(e);
    }
  };

  _handleMouseDown = (e: BasicEvent): void => {
    if (this.props.contentType !== OptionContentType.INPUT) {
      e.preventDefault(); // Prevent ChipSelectInput from hiding menu.
    }
  };

  _getContainerHeightOverride = (heightOverride?: MenuHeight): string | undefined => {
    if (heightOverride == null) {
      return;
    }

    if (heightOverride === MenuHeight.SMALL) {
      return css(styles.smallContainerHeight);
    } else if (heightOverride === MenuHeight.MEDIUM) {
      return css(styles.mediumContainerHeight);
    } else if (heightOverride === MenuHeight.LARGE) {
      return css(styles.largeContainerHeight);
    } else {
      return css(styles.xlargeContainerHeight);
    }
  };

  override render(): JSX.Element {
    // Use callback ref to forward parent's RefObject to the conditionally rendered element.
    // We can't use ref={this.props.optionRef} directly because:
    // 1. The element type (<a> or <div>) depends on contentType
    // 2. RefObjects are readonly, but callback refs can manually assign to .current
    const setOptionRef = (element: HTMLElement | null): void => {
      if (this.props.optionRef) {
        (this.props.optionRef as React.MutableRefObject<HTMLElement | null>).current = element;
      }
    };

    const optionContent = (): JSX.Element => {
      if (this.props.contentType === OptionContentType.ANCHOR) {
        return (
          <a
            ref={setOptionRef}
            role="menuitem"
            className={classNames(
              'menu-option',
              this.props.linkClassName,
              this._getContainerHeightOverride(this.props.heightOverride),
              {
                'is-uncontrolled-hover': this.props.isHovered == null,
                'is-hovered': this.props.isHovered != null && this.props.isHovered,
                'is-selected': this.props.isSelected,
                'is-disabled': this.props.isDisabled,
                'is-selectable': this._getOnSelectHandler() != null,
                [css(styles.border)]: this.props.hasBorder,
              }
            )}
            download={this.props.download}
            onClick={this._handleSelect}
            onMouseDown={this._handleMouseDown}
            onMouseOut={this.props.onMouseOut}
            onMouseOver={this.props.onHover}
            href={this.props.href}
            // tabIndex={0} is required for keyboard navigation. Without href (which is optional),
            // anchor elements are not focusable by default. Disabled options use tabIndex={-1}.
            tabIndex={this.props.isDisabled ? -1 : 0}
            {...(this.props.openInNewTab && {target: '_blank'})}
            {...getTestProps(this.props.dataTestAttrs)}
          >
            {this.props.children}
          </a>
        );
      }
      return (
        <div
          ref={setOptionRef}
          role="menuitem"
          className={classNames(
            'menu-option',
            this.props.linkClassName,
            this._getContainerHeightOverride(this.props.heightOverride),
            {
              'menu-option--contentTypeInput': this.props.contentType === OptionContentType.INPUT,
              'is-uncontrolled-hover': this.props.isHovered == null,
              'is-hovered': this.props.isHovered != null && this.props.isHovered,
              'is-selected': this.props.isSelected,
              'is-disabled': this.props.isDisabled,
              'is-selectable': this._getOnSelectHandler() != null,
              [css(styles.border)]: this.props.hasBorder,
            }
          )}
          onClick={this._handleSelect}
          onMouseDown={this._handleMouseDown}
          onMouseOver={this.props.onHover}
          onMouseOut={this.props.onMouseOut}
          onMouseUp={this.props.onMouseUp}
          tabIndex={this.props.isDisabled ? -1 : 0}
        >
          {this.props.children}
        </div>
      );
    };

    return (
      <li
        ref={this.containerRef}
        className={classNames('menu-option-container', this.props.className, {
          'menu-option--hasSubmenu': Boolean(this.props._submenu),
          'menu-option--hasSubmenu--isDisabled': Boolean(this.props._submenu) && this.props.isDisabled,
          ellipsize: this.props.shouldEllipsize,
        })}
        {...getTestProps({component: 'OptionContainer', element: 'root'})}
      >
        {optionContent()}
        {this.props._submenu}
      </li>
    );
  }
}

export default OptionContainer;

const styles = StyleSheet.create({
  border: {
    border: `0.5px solid ${GRAY_LIGHT}`,
  },
  smallContainerHeight: {
    minHeight: '28px',
  },
  mediumContainerHeight: {
    minHeight: '36px',
  },
  largeContainerHeight: {
    minHeight: '48px',
  },
  xlargeContainerHeight: {
    minHeight: '56px',
  },
});
